import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { invoicesApi } from '../../services/invoices';
import { casesApi } from '../../services/cases';
import { clientsApi } from '../../services/clients';
import { paymentsApi, PaymentWithRunningBalance, CreatePaymentRequest } from '../../services/payments';
import { creditsApi, CreateCreditRequest } from '../../services/credits';
import { Invoice, Case, Client, InvoiceStatus, PaymentMethod } from '../../types';

export const InvoiceDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [caseData, setCaseData] = useState<Case | null>(null);
  const [client, setClient] = useState<Client | null>(null);
  const [payments, setPayments] = useState<PaymentWithRunningBalance[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showLateFeePrompt, setShowLateFeePrompt] = useState(false);
  const [showCreditDialog, setShowCreditDialog] = useState(false);
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);
  const [creditSubmitting, setCreditSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Inline editing state for draft invoices
  const [editingIssueDate, setEditingIssueDate] = useState(false);
  const [editingDueDate, setEditingDueDate] = useState(false);
  const [editingLineItemRate, setEditingLineItemRate] = useState<number | null>(null);
  const [tempIssueDate, setTempIssueDate] = useState('');
  const [tempDueDate, setTempDueDate] = useState('');
  const [tempRate, setTempRate] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  // Payment form state
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('check');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');

  // Credit form state
  const [creditAmount, setCreditAmount] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const [creditInternalNotes, setCreditInternalNotes] = useState('');

  const paymentMethods: { value: PaymentMethod; label: string }[] = [
    { value: 'cash', label: 'Cash' },
    { value: 'check', label: 'Check' },
    { value: 'credit-card', label: 'Credit Card' },
    { value: 'zelle', label: 'Zelle' },
    { value: 'venmo', label: 'Venmo' },
    { value: 'paypal', label: 'PayPal' },
    { value: 'other', label: 'Other' },
  ];

  useEffect(() => {
    if (id) loadData(id);
  }, [id]);

  const loadData = async (invoiceId: string) => {
    try {
      setLoading(true);
      setError(null);
      const inv = await invoicesApi.get(invoiceId);
      // Add default values for new fields if they don't exist (for backwards compatibility)
      const invoiceWithDefaults = {
        ...inv,
        balanceDue: inv.balanceDue ?? inv.total,
        paidAmount: inv.paidAmount ?? 0,
        creditAmount: inv.creditAmount ?? 0,
      };
      setInvoice(invoiceWithDefaults);
      
      const [caseResult, clientResult, paymentsResult] = await Promise.all([
        casesApi.get(inv.caseId),
        clientsApi.get(inv.clientId),
        paymentsApi.list(invoiceId),
      ]);
      setCaseData(caseResult);
      setClient(clientResult);
      setPayments(paymentsResult.payments);
    } catch (err) {
      console.error('Failed to load invoice:', err);
      setError('Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const resetPaymentForm = () => {
    setPaymentAmount('');
    setPaymentDate(new Date().toISOString().split('T')[0]);
    setPaymentMethod('check');
    setPaymentReference('');
    setPaymentNotes('');
  };

  const resetCreditForm = () => {
    setCreditAmount('');
    setCreditReason('');
    setCreditInternalNotes('');
  };

  const handlePaymentSubmit = async () => {
    if (!invoice || !paymentAmount) return;
    
    const amountCents = Math.round(parseFloat(paymentAmount) * 100);
    if (isNaN(amountCents) || amountCents <= 0) {
      alert('Please enter a valid payment amount');
      return;
    }

    try {
      setPaymentSubmitting(true);
      const request: CreatePaymentRequest = {
        invoiceId: invoice.invoiceId,
        amount: amountCents,
        paymentDate,
        paymentMethod,
        reference: paymentReference || undefined,
        notes: paymentNotes || undefined,
      };
      
      const result = await paymentsApi.create(request);
      
      // Check if we should show late fee prompt
      const dueDate = new Date(invoice.dueDate);
      const payDate = new Date(paymentDate);
      const hasExistingBalance = client && (client.availableCredit ?? 0) < 0; // negative means they owe
      
      if (payDate > dueDate || hasExistingBalance) {
        setShowLateFeePrompt(true);
      }
      
      if (result.overpaymentCredit && result.overpaymentCredit > 0) {
        alert(`Payment recorded. $${(result.overpaymentCredit / 100).toFixed(2)} overpayment credit added to client account.`);
      }
      
      setShowPaymentDialog(false);
      resetPaymentForm();
      loadData(invoice.invoiceId);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to record payment';
      alert(errorMessage);
    } finally {
      setPaymentSubmitting(false);
    }
  };

  const handleCreditSubmit = async () => {
    if (!invoice || !creditAmount || !creditReason) return;
    
    const amountCents = Math.round(parseFloat(creditAmount) * 100);
    if (isNaN(amountCents) || amountCents <= 0) {
      alert('Please enter a valid credit amount');
      return;
    }

    if (amountCents > invoice.balanceDue) {
      alert('Credit adjustment cannot exceed invoice balance due');
      return;
    }

    try {
      setCreditSubmitting(true);
      const request: CreateCreditRequest = {
        invoiceId: invoice.invoiceId,
        amount: amountCents,
        reason: creditReason,
        internalNotes: creditInternalNotes || undefined,
      };
      
      await creditsApi.create(request);
      
      setShowCreditDialog(false);
      resetCreditForm();
      loadData(invoice.invoiceId);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to apply credit adjustment';
      alert(errorMessage);
    } finally {
      setCreditSubmitting(false);
    }
  };

  const handleSend = async () => {
    if (!invoice) return;
    try {
      setSending(true);
      setShowSendConfirm(false);
      const result = await invoicesApi.send(invoice.invoiceId);
      alert(`Invoice sent successfully to ${result.recipientEmail}`);
      loadData(invoice.invoiceId);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send invoice';
      alert(errorMessage);
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (newStatus: InvoiceStatus) => {
    if (!invoice) return;
    try {
      await invoicesApi.update(invoice.invoiceId, { status: newStatus });
      loadData(invoice.invoiceId);
    } catch (err) {
      alert('Failed to update invoice status');
    }
  };

  // Inline editing handlers for draft invoices
  const handleIssueDateEdit = () => {
    if (!invoice || invoice.status !== 'draft') return;
    setTempIssueDate(invoice.issueDate);
    setEditingIssueDate(true);
  };

  const handleIssueDateSave = async () => {
    if (!invoice || !tempIssueDate) return;
    try {
      setEditSaving(true);
      await invoicesApi.update(invoice.invoiceId, { issueDate: tempIssueDate });
      setEditingIssueDate(false);
      loadData(invoice.invoiceId);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update issue date';
      alert(errorMessage);
    } finally {
      setEditSaving(false);
    }
  };

  const handleIssueDateCancel = () => {
    setEditingIssueDate(false);
    setTempIssueDate('');
  };

  const handleDueDateEdit = () => {
    if (!invoice || invoice.status !== 'draft') return;
    setTempDueDate(invoice.dueDate);
    setEditingDueDate(true);
  };

  const handleDueDateSave = async () => {
    if (!invoice || !tempDueDate) return;
    try {
      setEditSaving(true);
      await invoicesApi.update(invoice.invoiceId, { dueDate: tempDueDate });
      setEditingDueDate(false);
      loadData(invoice.invoiceId);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update due date';
      alert(errorMessage);
    } finally {
      setEditSaving(false);
    }
  };

  const handleDueDateCancel = () => {
    setEditingDueDate(false);
    setTempDueDate('');
  };

  const handleLineItemRateEdit = (index: number) => {
    if (!invoice || invoice.status !== 'draft') return;
    const lineItem = invoice.lineItems[index];
    if (lineItem.rate !== undefined) {
      setTempRate((lineItem.rate / 100).toFixed(2));
      setEditingLineItemRate(index);
    }
  };

  const handleLineItemRateSave = async () => {
    if (!invoice || editingLineItemRate === null || !tempRate) return;
    const rateInCents = Math.round(parseFloat(tempRate) * 100);
    if (isNaN(rateInCents) || rateInCents <= 0) {
      alert('Please enter a valid positive rate');
      return;
    }
    try {
      setEditSaving(true);
      await invoicesApi.update(invoice.invoiceId, { 
        lineItems: [{ index: editingLineItemRate, rate: rateInCents }] 
      });
      setEditingLineItemRate(null);
      setTempRate('');
      loadData(invoice.invoiceId);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update rate';
      alert(errorMessage);
    } finally {
      setEditSaving(false);
    }
  };

  const handleLineItemRateCancel = () => {
    setEditingLineItemRate(null);
    setTempRate('');
  };

  const handleVoidPayment = async (paymentId: string) => {
    if (!invoice) return;
    if (!window.confirm('Are you sure you want to void this payment? The record will be retained for audit purposes.')) return;
    
    try {
      await paymentsApi.void(paymentId);
      loadData(invoice.invoiceId);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to void payment';
      alert(errorMessage);
    }
  };

  const handleDeletePayment = async (paymentId: string) => {
    if (!invoice) return;
    if (!window.confirm('Are you sure you want to permanently delete this payment? This action cannot be undone.')) return;
    
    try {
      await paymentsApi.delete(paymentId);
      loadData(invoice.invoiceId);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete payment';
      alert(errorMessage);
    }
  };

  const getPaymentMethodLabel = (method?: PaymentMethod): string => {
    if (!method) return '-';
    const found = paymentMethods.find(m => m.value === method);
    return found ? found.label : method;
  };


  const formatCurrency = (cents: number) => `$${(cents / 100).toFixed(2)}`;
  // Parse YYYY-MM-DD as local date to avoid timezone shift
  const formatDate = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString();
  };
  const formatDateTime = (dateStr: string) => new Date(dateStr).toLocaleString();

  const statusColors: Record<InvoiceStatus, string> = {
    draft: '#9e9e9e',
    sent: '#2196f3',
    paid: '#4caf50',
    overdue: '#f44336',
    cancelled: '#757575',
  };

  const canSend = invoice && invoice.status !== 'cancelled';
  const canRecordPayment = invoice && invoice.status !== 'cancelled' && invoice.balanceDue > 0;
  const canApplyCredit = invoice && invoice.status !== 'cancelled' && invoice.balanceDue > 0;

  if (loading) return <Layout><div style={{ padding: '24px' }}>Loading...</div></Layout>;
  if (error || !invoice) return <Layout><div style={{ padding: '24px', color: 'red' }}>{error || 'Invoice not found'}</div></Layout>;

  return (
    <Layout>
      <div style={{ padding: '24px', maxWidth: '900px', margin: '0 auto' }}>
        {/* Send Confirmation Modal */}
        {showSendConfirm && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', maxWidth: '400px', width: '90%' }}>
              <h3 style={{ marginTop: 0 }}>Send Invoice?</h3>
              <p style={{ color: '#666', marginBottom: '8px' }}>
                This will email invoice {invoice.invoiceNumber} to:
              </p>
              <p style={{ fontWeight: 500, marginBottom: '16px' }}>
                {client?.email || 'No email configured'}
              </p>
              {!client?.email && (
                <p style={{ color: '#f44336', fontSize: '14px', marginBottom: '16px' }}>
                  Warning: Client has no email address configured. Please add an email to the client record first.
                </p>
              )}
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button onClick={() => setShowSendConfirm(false)} style={{ padding: '8px 16px', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer', backgroundColor: 'white' }}>
                  Cancel
                </button>
                <button onClick={handleSend} disabled={!client?.email} style={{ backgroundColor: client?.email ? '#1976d2' : '#ccc', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: client?.email ? 'pointer' : 'not-allowed' }}>
                  Send Invoice
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Payment Entry Dialog */}
        {showPaymentDialog && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', maxWidth: '450px', width: '90%' }}>
              <h3 style={{ marginTop: 0 }}>Record Payment</h3>
              <p style={{ color: '#666', marginBottom: '16px' }}>
                Balance Due: ${(invoice.balanceDue / 100).toFixed(2)}
              </p>
              
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Amount *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  placeholder="0.00"
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '16px', boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Payment Date *</label>
                <input
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '16px', boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Payment Method *</label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '16px', boxSizing: 'border-box' }}
                >
                  {paymentMethods.map((method) => (
                    <option key={method.value} value={method.value}>{method.label}</option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Reference (optional)</label>
                <input
                  type="text"
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                  placeholder="Check #, transaction ID, etc."
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '16px', boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Notes (optional)</label>
                <textarea
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                  placeholder="Additional notes..."
                  rows={3}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '16px', boxSizing: 'border-box', resize: 'vertical' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button 
                  onClick={() => { setShowPaymentDialog(false); resetPaymentForm(); }} 
                  style={{ padding: '8px 16px', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer', backgroundColor: 'white' }}
                >
                  Cancel
                </button>
                <button 
                  onClick={handlePaymentSubmit} 
                  disabled={paymentSubmitting || !paymentAmount}
                  style={{ backgroundColor: paymentAmount ? '#4caf50' : '#ccc', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: paymentAmount ? 'pointer' : 'not-allowed' }}
                >
                  {paymentSubmitting ? 'Recording...' : 'Record Payment'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Late Fee Prompt */}
        {showLateFeePrompt && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', maxWidth: '400px', width: '90%' }}>
              <h3 style={{ marginTop: 0 }}>Add Late Fee?</h3>
              <p style={{ color: '#666', marginBottom: '16px' }}>
                This payment was received after the due date or the client has an existing balance. Would you like to add a late fee to a future invoice?
              </p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button 
                  onClick={() => setShowLateFeePrompt(false)} 
                  style={{ padding: '8px 16px', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer', backgroundColor: 'white' }}
                >
                  Skip
                </button>
                <button 
                  onClick={() => {
                    setShowLateFeePrompt(false);
                    // Navigate to create invoice with late fee (future implementation)
                    alert('Late fee workflow will be available when creating the next invoice for this client.');
                  }}
                  style={{ backgroundColor: '#ff9800', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }}
                >
                  Add Late Fee
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Credit Adjustment Dialog */}
        {showCreditDialog && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', maxWidth: '450px', width: '90%' }}>
              <h3 style={{ marginTop: 0 }}>Apply Credit Adjustment</h3>
              <p style={{ color: '#666', marginBottom: '16px' }}>
                Balance Due: ${(invoice.balanceDue / 100).toFixed(2)}
              </p>
              
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Amount *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={(invoice.balanceDue / 100).toFixed(2)}
                  value={creditAmount}
                  onChange={(e) => setCreditAmount(e.target.value)}
                  placeholder="0.00"
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '16px', boxSizing: 'border-box' }}
                />
                <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#666' }}>
                  Maximum: ${(invoice.balanceDue / 100).toFixed(2)}
                </p>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Reason *</label>
                <input
                  type="text"
                  value={creditReason}
                  onChange={(e) => setCreditReason(e.target.value)}
                  placeholder="e.g., Discount, Write-off, Goodwill credit"
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '16px', boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Internal Notes (optional)</label>
                <textarea
                  value={creditInternalNotes}
                  onChange={(e) => setCreditInternalNotes(e.target.value)}
                  placeholder="Notes visible only to advocates (hidden from client portal)"
                  rows={3}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '16px', boxSizing: 'border-box', resize: 'vertical' }}
                />
                <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#666' }}>
                  These notes will not be visible to the client.
                </p>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button 
                  onClick={() => { setShowCreditDialog(false); resetCreditForm(); }} 
                  style={{ padding: '8px 16px', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer', backgroundColor: 'white' }}
                >
                  Cancel
                </button>
                <button 
                  onClick={handleCreditSubmit} 
                  disabled={creditSubmitting || !creditAmount || !creditReason}
                  style={{ backgroundColor: (creditAmount && creditReason) ? '#9c27b0' : '#ccc', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: (creditAmount && creditReason) ? 'pointer' : 'not-allowed' }}
                >
                  {creditSubmitting ? 'Applying...' : 'Apply Credit'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
          <div>
            <button onClick={() => navigate(`/cases/${invoice.caseId}`)} style={{ background: 'none', border: 'none', color: '#1976d2', cursor: 'pointer', marginBottom: '8px', padding: 0 }}>
              ← Back to Case
            </button>
            <h1 style={{ margin: 0 }}>{invoice.invoiceNumber}</h1>
            <div style={{ marginTop: '8px' }}>
              <span style={{ backgroundColor: statusColors[invoice.status], color: 'white', padding: '4px 12px', borderRadius: '12px', fontSize: '14px', textTransform: 'uppercase' }}>
                {invoice.status}
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {canRecordPayment && (
              <button onClick={() => setShowPaymentDialog(true)} style={{ backgroundColor: '#4caf50', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}>
                Record Payment
              </button>
            )}
            {canApplyCredit && (
              <button onClick={() => setShowCreditDialog(true)} style={{ backgroundColor: '#9c27b0', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}>
                Apply Credit
              </button>
            )}
            {canSend && (
              <button onClick={() => setShowSendConfirm(true)} disabled={sending} style={{ backgroundColor: '#1976d2', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}>
                {sending ? 'Sending...' : invoice.sentAt ? 'Resend Invoice' : 'Send Invoice'}
              </button>
            )}
            {invoice.status === 'sent' && (
              <button onClick={() => handleStatusChange('paid')} style={{ backgroundColor: '#4caf50', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}>
                Mark as Paid
              </button>
            )}
          </div>
        </div>


        {/* Invoice Info Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '24px' }}>
          <div style={{ backgroundColor: '#f5f5f5', padding: '16px', borderRadius: '8px' }}>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              Issue Date
              {invoice.status === 'draft' && !editingIssueDate && (
                <button 
                  onClick={handleIssueDateEdit}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: '#1976d2', fontSize: '14px' }}
                  title="Edit issue date"
                >
                  ✎
                </button>
              )}
            </div>
            {editingIssueDate ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="date"
                  value={tempIssueDate}
                  onChange={(e) => setTempIssueDate(e.target.value)}
                  style={{ padding: '4px 8px', border: '1px solid #1976d2', borderRadius: '4px', fontSize: '14px' }}
                  disabled={editSaving}
                />
                <button 
                  onClick={handleIssueDateSave}
                  disabled={editSaving}
                  style={{ background: '#4caf50', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', fontSize: '12px' }}
                >
                  {editSaving ? '...' : '✓'}
                </button>
                <button 
                  onClick={handleIssueDateCancel}
                  disabled={editSaving}
                  style={{ background: '#f44336', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', fontSize: '12px' }}
                >
                  ✕
                </button>
              </div>
            ) : (
              <div style={{ fontSize: '18px', fontWeight: 500 }}>{formatDate(invoice.issueDate)}</div>
            )}
          </div>
          <div style={{ backgroundColor: '#f5f5f5', padding: '16px', borderRadius: '8px' }}>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              Due Date
              {invoice.status === 'draft' && !editingDueDate && (
                <button 
                  onClick={handleDueDateEdit}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: '#1976d2', fontSize: '14px' }}
                  title="Edit due date"
                >
                  ✎
                </button>
              )}
            </div>
            {editingDueDate ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="date"
                  value={tempDueDate}
                  onChange={(e) => setTempDueDate(e.target.value)}
                  style={{ padding: '4px 8px', border: '1px solid #1976d2', borderRadius: '4px', fontSize: '14px' }}
                  disabled={editSaving}
                />
                <button 
                  onClick={handleDueDateSave}
                  disabled={editSaving}
                  style={{ background: '#4caf50', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', fontSize: '12px' }}
                >
                  {editSaving ? '...' : '✓'}
                </button>
                <button 
                  onClick={handleDueDateCancel}
                  disabled={editSaving}
                  style={{ background: '#f44336', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', fontSize: '12px' }}
                >
                  ✕
                </button>
              </div>
            ) : (
              <div style={{ fontSize: '18px', fontWeight: 500 }}>{formatDate(invoice.dueDate)}</div>
            )}
          </div>
          <div style={{ backgroundColor: '#e3f2fd', padding: '16px', borderRadius: '8px' }}>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Total Amount</div>
            <div style={{ fontSize: '24px', fontWeight: 600, color: '#1976d2' }}>{formatCurrency(invoice.total)}</div>
          </div>
        </div>

        {/* Sent Status */}
        {invoice.sentAt && (
          <div style={{ backgroundColor: '#e8f5e9', padding: '12px 16px', borderRadius: '8px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: '#4caf50', fontSize: '18px' }}>✓</span>
            <span style={{ color: '#2e7d32' }}>Invoice sent on {formatDateTime(invoice.sentAt)}</span>
          </div>
        )}

        {/* Client & Case Info */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
          <div style={{ backgroundColor: '#f5f5f5', padding: '16px', borderRadius: '8px' }}>
            <h3 style={{ marginTop: 0, marginBottom: '12px' }}>Bill To</h3>
            {client && (
              <>
                <p style={{ margin: '4px 0', fontWeight: 500 }}>{client.firstName} {client.lastName}</p>
                <p style={{ margin: '4px 0', color: '#666' }}>{client.email || 'No email'}</p>
                <p style={{ margin: '4px 0', color: '#666' }}>{client.phone || ''}</p>
              </>
            )}
          </div>
          <div style={{ backgroundColor: '#f5f5f5', padding: '16px', borderRadius: '8px' }}>
            <h3 style={{ marginTop: 0, marginBottom: '12px' }}>Case</h3>
            {caseData && (
              <>
                <p style={{ margin: '4px 0', fontWeight: 500 }}>{caseData.childFirstName} {caseData.childLastName}</p>
                <p style={{ margin: '4px 0', color: '#666' }}>{caseData.school || ''}</p>
                <p style={{ margin: '4px 0', color: '#666' }}>{caseData.schoolDistrict || ''}</p>
              </>
            )}
          </div>
        </div>

        {/* Line Items */}
        <div style={{ backgroundColor: 'white', border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden', marginBottom: '24px' }}>
          <h3 style={{ margin: 0, padding: '16px', borderBottom: '1px solid #e0e0e0', backgroundColor: '#fafafa' }}>Line Items</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#fafafa' }}>
                <th style={{ textAlign: 'left', padding: '12px 16px', borderBottom: '1px solid #e0e0e0' }}>Date</th>
                <th style={{ textAlign: 'left', padding: '12px 16px', borderBottom: '1px solid #e0e0e0' }}>Description</th>
                <th style={{ textAlign: 'right', padding: '12px 16px', borderBottom: '1px solid #e0e0e0' }}>Duration</th>
                <th style={{ textAlign: 'right', padding: '12px 16px', borderBottom: '1px solid #e0e0e0' }}>Rate</th>
                <th style={{ textAlign: 'right', padding: '12px 16px', borderBottom: '1px solid #e0e0e0' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {invoice.lineItems.map((item, idx) => (
                <tr key={idx} style={{ borderBottom: idx < invoice.lineItems.length - 1 ? '1px solid #e0e0e0' : 'none' }}>
                  <td style={{ padding: '12px 16px' }}>{formatDate(item.date)}</td>
                  <td style={{ padding: '12px 16px' }}>{item.description}</td>
                  <td style={{ padding: '12px 16px', textAlign: 'right' }}>{item.duration ? `${Math.floor(item.duration / 60)}h ${item.duration % 60}m` : '-'}</td>
                  <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                    {editingLineItemRate === idx ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px' }}>
                        <span>$</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0.01"
                          value={tempRate}
                          onChange={(e) => setTempRate(e.target.value)}
                          style={{ width: '80px', padding: '4px 8px', border: '1px solid #1976d2', borderRadius: '4px', fontSize: '14px', textAlign: 'right' }}
                          disabled={editSaving}
                        />
                        <span>/hr</span>
                        <button 
                          onClick={handleLineItemRateSave}
                          disabled={editSaving}
                          style={{ background: '#4caf50', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 6px', cursor: 'pointer', fontSize: '12px', marginLeft: '4px' }}
                        >
                          {editSaving ? '...' : '✓'}
                        </button>
                        <button 
                          onClick={handleLineItemRateCancel}
                          disabled={editSaving}
                          style={{ background: '#f44336', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 6px', cursor: 'pointer', fontSize: '12px' }}
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        {item.rate ? `${formatCurrency(item.rate)}/hr` : '-'}
                        {invoice.status === 'draft' && item.rate !== undefined && (
                          <button 
                            onClick={() => handleLineItemRateEdit(idx)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: '#1976d2', fontSize: '14px' }}
                            title="Edit rate"
                          >
                            ✎
                          </button>
                        )}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 500 }}>{formatCurrency(item.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>


        {/* Totals */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '24px' }}>
          <div style={{ width: '300px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e0e0e0' }}>
              <span style={{ color: '#666' }}>Subtotal</span>
              <span>{formatCurrency(invoice.subtotal)}</span>
            </div>
            {invoice.taxRate > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e0e0e0' }}>
                <span style={{ color: '#666' }}>Tax ({invoice.taxRate}%)</span>
                <span>{formatCurrency(invoice.taxAmount)}</span>
              </div>
            )}
            {invoice.paidAmount > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e0e0e0' }}>
                <span style={{ color: '#666' }}>Payments</span>
                <span style={{ color: '#4caf50' }}>-{formatCurrency(invoice.paidAmount)}</span>
              </div>
            )}
            {invoice.creditAmount > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e0e0e0' }}>
                <span style={{ color: '#666' }}>Credits</span>
                <span style={{ color: '#4caf50' }}>-{formatCurrency(invoice.creditAmount)}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', fontWeight: 600, fontSize: '18px' }}>
              <span>Total</span>
              <span style={{ color: '#1976d2' }}>{formatCurrency(invoice.total)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600, fontSize: '18px', backgroundColor: invoice.balanceDue > 0 ? '#fff3e0' : '#e8f5e9', margin: '8px -8px 0', padding: '12px 8px', borderRadius: '4px' }}>
              <span>Balance Due</span>
              <span style={{ color: invoice.balanceDue > 0 ? '#f57c00' : '#4caf50' }}>{formatCurrency(invoice.balanceDue)}</span>
            </div>
          </div>
        </div>

        {/* Payment History */}
        {payments.length > 0 && (
          <div style={{ backgroundColor: 'white', border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden', marginBottom: '24px' }}>
            <h3 style={{ margin: 0, padding: '16px', borderBottom: '1px solid #e0e0e0', backgroundColor: '#fafafa' }}>Payment History</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#fafafa' }}>
                  <th style={{ textAlign: 'left', padding: '12px 16px', borderBottom: '1px solid #e0e0e0' }}>Date</th>
                  <th style={{ textAlign: 'left', padding: '12px 16px', borderBottom: '1px solid #e0e0e0' }}>Type</th>
                  <th style={{ textAlign: 'left', padding: '12px 16px', borderBottom: '1px solid #e0e0e0' }}>Method</th>
                  <th style={{ textAlign: 'left', padding: '12px 16px', borderBottom: '1px solid #e0e0e0' }}>Reference</th>
                  <th style={{ textAlign: 'right', padding: '12px 16px', borderBottom: '1px solid #e0e0e0' }}>Amount</th>
                  <th style={{ textAlign: 'right', padding: '12px 16px', borderBottom: '1px solid #e0e0e0' }}>Balance</th>
                  <th style={{ textAlign: 'center', padding: '12px 16px', borderBottom: '1px solid #e0e0e0' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((payment, idx) => (
                  <tr 
                    key={payment.paymentId} 
                    style={{ 
                      borderBottom: idx < payments.length - 1 ? '1px solid #e0e0e0' : 'none',
                      backgroundColor: payment.status === 'voided' ? '#f5f5f5' : 'white',
                      opacity: payment.status === 'voided' ? 0.7 : 1
                    }}
                  >
                    <td style={{ padding: '12px 16px' }}>{formatDate(payment.paymentDate)}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ 
                        display: 'inline-flex', 
                        alignItems: 'center', 
                        gap: '4px' 
                      }}>
                        {payment.type === 'credit' ? 'Credit' : 'Payment'}
                        {payment.status === 'voided' && (
                          <span style={{ 
                            backgroundColor: '#f44336', 
                            color: 'white', 
                            padding: '2px 6px', 
                            borderRadius: '4px', 
                            fontSize: '10px',
                            textTransform: 'uppercase'
                          }}>
                            Voided
                          </span>
                        )}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px' }}>{payment.type === 'credit' ? (payment.reason || '-') : getPaymentMethodLabel(payment.paymentMethod)}</td>
                    <td style={{ padding: '12px 16px', color: '#666' }}>{payment.reference || '-'}</td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 500, color: payment.status === 'voided' ? '#999' : '#4caf50' }}>
                      {payment.status === 'voided' ? <s>{formatCurrency(payment.amount)}</s> : formatCurrency(payment.amount)}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', color: '#666' }}>
                      {formatCurrency(payment.runningBalance)}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      {payment.status === 'active' && payment.type === 'payment' && (
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                          <button
                            onClick={() => handleVoidPayment(payment.paymentId)}
                            style={{ 
                              background: 'none', 
                              border: '1px solid #ff9800', 
                              color: '#ff9800', 
                              padding: '4px 8px', 
                              borderRadius: '4px', 
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            Void
                          </button>
                          <button
                            onClick={() => handleDeletePayment(payment.paymentId)}
                            style={{ 
                              background: 'none', 
                              border: '1px solid #f44336', 
                              color: '#f44336', 
                              padding: '4px 8px', 
                              borderRadius: '4px', 
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Notes */}
        {invoice.notes && (
          <div style={{ backgroundColor: '#fff9c4', padding: '16px', borderRadius: '8px' }}>
            <h4 style={{ margin: '0 0 8px 0' }}>Notes</h4>
            <p style={{ margin: 0, color: '#666' }}>{invoice.notes}</p>
          </div>
        )}
      </div>
    </Layout>
  );
};
